import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TextareaAutosize, Input, Typography } from "@mui/material";
import {
  Globe,
  Info,
  PencilIcon,
  Plus,
  Sparkles,
  Workflow,
} from "lucide-react";
import { ButtonGroup as ModeSelection } from "../../../components/ui/button-group";
import { Tooltip } from "@/components/ui/tooltip-card";

import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import z from "zod";
import { usePlaygroundParametersStore } from "@/atoms/playground";
import { usePlaygroundProject } from "@/atoms/playground-project";
import { PlaygroundProjectSelect } from "@/pages/Playground/components/playground-project-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ModelMenu from "./ModelMenu";
import { useQuery } from "@tanstack/react-query";
import { IDefaultModel, Model } from "@/types";
import { llmApi } from "@/services/rag-service";
import { SliderWithLabel } from "@/components/ui/slider-with-label";
import { useSaveBlueprint } from "../hooks/useBlueprintMutations";
import {
  blueprintsApi,
  getBlueprintApiErrorMessage,
  type CreateBlueprintPayload,
} from "@/services/blueprints-service";
import BlueprintAgentToolsPicker from "./BlueprintAgentToolsPicker";
import BlueprintAttachments from "./BlueprintAttachments";
import { toast } from "react-toastify";
import { Spinner } from "@/components/ui/spinner";
interface LLMConfigsResponse {
  items: Array<Model>;
  total_count: number;
}
const modes = [
  {
    value: "agentic",
    labelKey: "playground.agentic",
    icon: <Sparkles className="h-3 w-3" />,
    tooltipKey: "playground.agenticTooltip",
  },
  {
    value: "planner",
    labelKey: "playground.planner",
    icon: <Workflow className="h-3 w-3" />,
    tooltipKey: "playground.plannerTooltip",
  },
];

export type BlueprintDetailsDialogProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSubmit?: (values: FullBlueprintFormValues) => void;
  blueprint?: {
    id: string;
  };
};

const fullBlueprintSchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  project_id: z.string().min(1, "Project is required"),
  instructions: z.string().trim().min(1, "Instructions are required"),
  description: z.string().optional(),
  default_base_llm_config_id: z.string(),
  default_planner_llm_config_id: z.string().optional(),
  default_mode: z.string(),
  default_language: z.string(),
  default_tools: z.array(z.string()).optional(),
  // default_temperature: z.number().optional(),
  // default_max_tokens: z.number().optional(),
  default_document_ids: z.array(z.string()).optional(),
  default_collection_ids: z.array(z.string()).optional(),
  default_mcp_connection_ids: z.array(z.string()).optional(),
  default_mcp_tool_ids: z.array(z.string()).optional(),
  // Add other fields as necessary
});
type FullBlueprintFormValues = z.infer<typeof fullBlueprintSchema>;

const BlueprintDetailsDialog = ({
  open,
  onOpenChange,
  onSubmit,
  blueprint,
}: BlueprintDetailsDialogProps) => {
  const { t, i18n } = useTranslation();
  const { projectId, setProjectId } = usePlaygroundProject();
  const [instructionsDialogOpen, setInstructionsDialogOpen] = useState(false);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const [attachmentResetKey, setAttachmentResetKey] = useState(0);
  const [requiredErrors, setRequiredErrors] = useState({
    name: "",
    project_id: "",
    instructions: "",
  });
  const saveBlueprint = useSaveBlueprint();

  const { data: fetchedBlueprint, isLoading: isBlueprintLoading } = useQuery({
    queryKey: ["blueprint", blueprint?.id],
    queryFn: async () => {
      try {
        return await blueprintsApi.getBlueprint(blueprint!.id);
      } catch (error) {
        toast.error(
          getBlueprintApiErrorMessage(error, "Failed to load blueprint"),
        );
        throw error;
      }
    },
    enabled: Boolean(open && blueprint?.id),
  });
  const {
    llm_config_id,
    planner_llm_config,
    mode,
    // temperature,
    // max_tokens,
    language,
  } = usePlaygroundParametersStore();
  const { data: models } = useQuery<Array<Model>>({
    queryKey: ["llm-configs"],
    queryFn: () => {
      return llmApi.getLLMConfigs().then((response) => {
        const { items } = response as unknown as LLMConfigsResponse;
        const models =
          items?.filter((model) => !model.is_embedding_model) ?? [];

        return models;
      });
    },
  });

  const { data: defaultSelectedModels } = useQuery<{
    defaults: IDefaultModel[];
  }>({
    queryKey: ["default-models"],
    queryFn: llmApi.getDefaultModels,
  });

  const languages = [
    { value: "auto", label: t("playground.auto") },
    { value: "en", label: t("playground.english") },
    { value: "ar", label: t("playground.arabic") },
  ];

  const {
    register,
    clearErrors,
    getValues,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FullBlueprintFormValues>({
    defaultValues: {
      name: "",
      project_id: projectId,
      instructions: "",
      description: "",
      default_base_llm_config_id: llm_config_id || "",
      default_planner_llm_config_id: planner_llm_config || "",
      default_mode: mode || "agentic",
      default_language: language || "auto",
      default_tools: [],
      // default_temperature: temperature,
      // default_max_tokens: max_tokens,
      default_document_ids: [],
      default_collection_ids: [],
      default_mcp_connection_ids: [],
      default_mcp_tool_ids: [],
    },
  });
  useEffect(() => {
    if (!fetchedBlueprint) return;

    reset({
      name: fetchedBlueprint.name,
      project_id: fetchedBlueprint.project_id || "",
      instructions: fetchedBlueprint.instructions,
      description: fetchedBlueprint.description,
      default_base_llm_config_id:
        fetchedBlueprint.default_base_llm_config_id || "",
      default_planner_llm_config_id:
        fetchedBlueprint.default_planner_llm_config_id || "",
      default_mode: fetchedBlueprint.default_mode,
      default_language: fetchedBlueprint.default_language,
      default_tools: fetchedBlueprint.default_tools || [],
      // default_temperature: fetchedBlueprint.default_temperature ?? undefined,
      // default_max_tokens: fetchedBlueprint.default_max_tokens ?? undefined,
      default_document_ids: fetchedBlueprint.default_document_ids || [],
      default_collection_ids: fetchedBlueprint.default_collection_ids || [],
      default_mcp_connection_ids:
        fetchedBlueprint.default_mcp_connection_ids || [],
      default_mcp_tool_ids: fetchedBlueprint.default_mcp_tool_ids || [],
    });
    setProjectId(fetchedBlueprint.project_id || "");
  }, [fetchedBlueprint, reset, setProjectId]);
  useEffect(() => {
    setValue("project_id", projectId);
    if (projectId) {
      clearErrors("project_id");
      setRequiredErrors((current) => ({ ...current, project_id: "" }));
    }
  }, [clearErrors, projectId, setValue]);
  const model = models?.find(
    (model) => model.id === watch("default_base_llm_config_id"),
  );
  const handleReset = useCallback(() => {
    reset({
      name: "",
      project_id: projectId,
      instructions: "",
      description: "",
      default_base_llm_config_id: llm_config_id,
      default_planner_llm_config_id: "",
      default_mode: "agentic",
      default_language: "auto",
      default_tools: [],
      // default_temperature: undefined,
      // default_max_tokens: undefined,
      default_document_ids: [],
      default_collection_ids: [],
      default_mcp_connection_ids: [],
      default_mcp_tool_ids: [],
    });
    setAttachmentFiles([]);
    setAttachmentResetKey((current) => current + 1);
  }, [llm_config_id, projectId, reset]);
  const handleDialogOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        handleReset();
        setRequiredErrors({ name: "", project_id: "", instructions: "" });
        setInstructionsDialogOpen(false);
      }
      onOpenChange?.(nextOpen);
    },
    [handleReset, onOpenChange],
  );
  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const nameIsEmpty = !getValues("name").trim();
      const projectIsEmpty = !getValues("project_id").trim();
      const instructionsAreEmpty = !getValues("instructions").trim();
      setRequiredErrors({
        name: nameIsEmpty ? t("blueprints.fields.nameIsRequired") : "",
        project_id: projectIsEmpty
          ? t("blueprints.fields.projectIsRequired")
          : "",
        instructions: instructionsAreEmpty
          ? t("blueprints.fields.instructionsAreRequired")
          : "",
      });
      if (nameIsEmpty || projectIsEmpty || instructionsAreEmpty) {
        if (instructionsAreEmpty) setInstructionsDialogOpen(true);
        return;
      }
      const values = getValues();
      const availableModelIds = new Set(
        models?.map((available) => available.id),
      );
      const baseModelId =
        values.default_base_llm_config_id &&
        availableModelIds.has(values.default_base_llm_config_id)
          ? values.default_base_llm_config_id
          : null;
      const plannerModelId =
        values.default_planner_llm_config_id &&
        availableModelIds.has(values.default_planner_llm_config_id)
          ? values.default_planner_llm_config_id
          : null;
      const payload: CreateBlueprintPayload = {
        name: values.name,
        project_id: values.project_id,
        description: values.description || "",
        instructions: values.instructions,
        default_base_llm_config_id: baseModelId,
        ...(values.default_mode === "planner" && plannerModelId
          ? { default_planner_llm_config_id: plannerModelId }
          : {}),
        default_mode: values.default_mode,
        default_language: values.default_language,
        default_tools: values.default_tools?.length
          ? values.default_tools
          : null,
        // default_temperature and default_max_tokens are no longer sent to the API
        // default_temperature: null,
        // default_max_tokens: null,
        default_document_ids: values.default_document_ids?.length
          ? values.default_document_ids
          : null,
        default_collection_ids: values.default_collection_ids?.length
          ? values.default_collection_ids
          : null,
        default_mcp_connection_ids: values.default_mcp_connection_ids?.length
          ? values.default_mcp_connection_ids
          : null,
        default_mcp_tool_ids: values.default_mcp_tool_ids?.length
          ? values.default_mcp_tool_ids
          : null,
        sharing: { scope: "inherit" },
      };

      try {
        await saveBlueprint.mutateAsync({
          id: blueprint?.id,
          payload,
          files: blueprint?.id ? undefined : attachmentFiles,
        });
      } catch (error) {
        toast.error(
          getBlueprintApiErrorMessage(error, "Failed to save blueprint"),
        );
        return;
      }
      if (!blueprint?.id) {
        handleReset();
      }
      onSubmit?.(values);
      onOpenChange?.(false);
    },
    [
      attachmentFiles,
      blueprint?.id,
      getValues,
      handleReset,
      models,
      onOpenChange,
      onSubmit,
      saveBlueprint,
      t,
    ],
  );
  const handleDiscard = useCallback(() => {
    handleDialogOpenChange(false);
  }, [handleDialogOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-h-[90vh] gap-0 overflow-y-auto sm:max-w-[640px] p-0">
        <div className="px-6 pt-4">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">
              {blueprint?.id
                ? t(`blueprints.EditBlueprint`)
                : t(`blueprints.CreateBlueprint`)}
            </DialogTitle>
          </DialogHeader>
        </div>

        <form
          onSubmit={handleSubmit}
          className="mt-6 flex min-w-0 flex-col gap-5"
        >
          <div className={`grid gap-4 px-6 sm:grid-cols-2`}>
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="blueprint-name"
                className="text-foreground text-sm font-semibold"
              >
                {t("blueprints.fields.name")}
              </label>
              <Input
                id="blueprint-name"
                disableUnderline
                placeholder={t("blueprints.fields.namePlaceholder")}
                className={`bg-input border text-foreground placeholder:text-muted-foreground h-9 rounded-[8px] px-3 py-2 ${
                  errors.name
                    ? "border-destructive focus-visible:ring-destructive/20"
                    : "border-border"
                }`}
                aria-invalid={Boolean(errors.name)}
                {...register("name", {
                  onChange: (event) => {
                    if (event.target.value.trim()) {
                      clearErrors("name");
                      setRequiredErrors((current) => ({
                        ...current,
                        name: "",
                      }));
                    }
                  },
                })}
              />
              {(errors.name?.message || requiredErrors.name) && (
                <p className="text-sm text-red-500">
                  {errors.name?.message || requiredErrors.name}
                </p>
              )}
            </div>
            {
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="blueprint-project"
                  className="text-foreground text-sm font-semibold"
                >
                  {t("blueprints.fields.project")}
                </label>
                <PlaygroundProjectSelect disabled={Boolean(blueprint?.id)} />
                {(errors.project_id?.message || requiredErrors.project_id) && (
                  <p className="text-sm text-red-500">
                    {errors.project_id?.message || requiredErrors.project_id}
                  </p>
                )}
              </div>
            }
          </div>

          <div className="border-t border-border"></div>

          <div className="flex min-w-0 flex-col gap-1.5 px-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-1">
                  <Typography
                    variant="body2"
                    className="text-foreground text-sm font-semibold"
                  >
                    {t("blueprints.instructions")}
                  </Typography>
                  <Tooltip
                    content={
                      <div className="space-y-1 text-sm">
                        <p>{t("blueprints.instructionsToolTip")}</p>
                      </div>
                    }
                  >
                    <Info className="size-3.5 text-muted-foreground" />
                  </Tooltip>
                </div>
                <Typography
                  variant="body2"
                  className="text-sm text-muted-foreground"
                >
                  {t(
                    "blueprints.fields.instructionsDescription",
                    "Add instructions to tailor the blueprint's responses",
                  )}
                </Typography>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setInstructionsDialogOpen(true)}
                className="size-6 rounded-[4px] bg-secondary text-foreground hover:bg-secondary/80"
                aria-label={t(
                  "blueprints.fields.addInstructions",
                  "Add instructions",
                )}
              >
                {watch("instructions") ? (
                  <PencilIcon className="size-4" />
                ) : (
                  <Plus className="size-4" />
                )}
              </Button>
            </div>
            {watch("instructions") && (
              <p className="max-h-[30vh] min-w-0 max-w-full overflow-auto whitespace-pre-wrap break-words text-sm text-foreground pt-3">
                {watch("instructions")}
              </p>
            )}
            {(errors.instructions?.message || requiredErrors.instructions) && (
              <p className="text-sm text-red-500">
                {errors.instructions?.message || requiredErrors.instructions}
              </p>
            )}
          </div>

          <div className="border-t border-border"></div>

          <div className="flex flex-col gap-1.5 px-6">
            <div>
              <Typography
                variant="body2"
                className="text-foreground text-sm font-semibold"
              >
                {t("blueprints.configurations")}
              </Typography>
              <Typography
                variant="body2"
                className="text-sm text-muted-foreground"
              >
                {t("blueprints.tunePreferences")}
              </Typography>
            </div>

            <div className="grid *:grid-cols-1 gap-4 sm:grid-cols-2 pt-3">
              {/* <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="default-mode"
                  className="text-sm text-muted-foreground"
                >
                  Default Mode
                </label>
                <ModeSelection
                  value={watch("default_mode")}
                  onValueChange={(value) => {
                    setValue("default_mode", value);
                  }}
                  options={modes.map((mode) => ({
                    ...mode,
                    label: t(mode.labelKey),
                    icon: mode.icon, // Correctly passing the icon property
                    tooltip: t(mode.tooltipKey), // Passing the tooltip property
                  }))}
                />
              </div> */}

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="blueprint-language"
                  className="text-sm text-muted-foreground"
                >
                  {t("blueprints.preferredLanguage")}
                </label>

                <Select
                  dir={i18n.dir()}
                  value={watch("default_language")}
                  onValueChange={(value) => setValue("default_language", value)}
                >
                  <SelectTrigger className="w-full bg-input text-foreground placeholder:text-muted-foreground h-11 rounded-[8px] px-4 py-3 border border-border">
                    <span className="min-w-0 flex items-center gap-2">
                      <Globe className="size-4 shrink-0 text-foreground" />
                      <SelectValue placeholder={t("playground.auto")} />
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map((language) => (
                      <SelectItem key={language.value} value={language.value}>
                        {language.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="blueprint-base-llm-config"
                  className="text-sm text-muted-foreground"
                >
                  {t("blueprints.baseModel")}
                </label>

                <ModelMenu
                  type="base"
                  models={models}
                  selectedModel={watch("default_base_llm_config_id")}
                  defaultModels={defaultSelectedModels?.defaults}
                  onModelChange={(modelId) =>
                    setValue("default_base_llm_config_id", modelId)
                  }
                  isBlueprintDialog={true}
                />
              </div>

              {getValues("default_mode") === "planner" && (
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="blueprint-planner-llm-config"
                    className="text-sm text-muted-foreground"
                  >
                    Planner Model
                  </label>

                  <ModelMenu
                    type="planner"
                    models={models}
                    selectedModel={watch("default_planner_llm_config_id")}
                    defaultModels={defaultSelectedModels?.defaults}
                    onModelChange={(modelId) =>
                      setValue("default_planner_llm_config_id", modelId)
                    }
                  />
                </div>
              )}

              {/* <div className="flex flex-col gap-1.5">
                <SliderWithLabel
                  label={t("playground.temperature")}
                  min={0}
                  max={1}
                  step={0.01}
                  value={watch("default_temperature") || 0}
                  onChange={(value) => setValue("default_temperature", value)}
                  padding="0px 4px 0px 0px"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <SliderWithLabel
                  label={t("playground.maximumTokens")}
                  min={0}
                  max={model?.max_tokens || 4096}
                  step={Math.max(
                    1,
                    Math.floor((model?.max_tokens || 4096) / 100),
                  )}
                  value={watch("default_max_tokens") || 0}
                  onChange={(value) => setValue("default_max_tokens", value)}
                  padding="0px 4px 0px 0px"
                />
              </div> */}
            </div>
          </div>

          <div className="border-t border-border"></div>

          <div className="flex flex-col gap-1.5 px-6">
            <BlueprintAgentToolsPicker
              selectedTools={watch("default_tools")}
              selectedCollectionIds={watch("default_collection_ids")}
              selectedDocumentIds={watch("default_document_ids")}
              selectedMcpConnectionIds={watch("default_mcp_connection_ids")}
              selectedMcpToolIds={watch("default_mcp_tool_ids")}
              onChange={({
                tools,
                collectionIds,
                documentIds,
                mcpConnectionIds,
                mcpToolIds,
              }) => {
                setValue("default_tools", tools);
                setValue("default_collection_ids", collectionIds);
                setValue("default_document_ids", documentIds);
                setValue("default_mcp_connection_ids", mcpConnectionIds);
                setValue("default_mcp_tool_ids", mcpToolIds);
              }}
            />
          </div>

          <div className="border-t border-border"></div>

          <div className="flex flex-col gap-1.5 px-6">
            <BlueprintAttachments
              blueprintId={blueprint?.id}
              resetKey={attachmentResetKey}
              onFilesChange={setAttachmentFiles}
              onUpload={(file) =>
                blueprintsApi.uploadBlueprintDocument(blueprint!.id, file)
              }
              onUploadingChange={setIsUploadingAttachments}
              instructions={watch("instructions")}
              onInstructionsChange={(value) => setValue("instructions", value)}
            />
          </div>

          <DialogFooter className="mt-2 flex justify-end gap-2 px-6 pb-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleDiscard}
              className="border-border bg-card text-foreground hover:bg-accent px-6 rounded-[8px]"
            >
              {t("common.discard")}
            </Button>
            <Button
              type="submit"
              disabled={
                saveBlueprint.isPending ||
                isUploadingAttachments ||
                isBlueprintLoading
              }
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-6 rounded-[8px]"
            >
              {saveBlueprint.isPending ? (
                <>
                  <Spinner className="size-4" />
                  <span>
                    {blueprint?.id ? t("common.saving") : t("common.creating")}
                  </span>
                </>
              ) : blueprint?.id ? (
                t("common.save", "Save")
              ) : (
                t("common.create", "Create")
              )}
            </Button>
          </DialogFooter>
        </form>

        <Dialog
          open={instructionsDialogOpen}
          onOpenChange={setInstructionsDialogOpen}
        >
          <DialogContent className="sm:max-w-[560px]">
            <DialogHeader>
              <DialogTitle>
                {t("blueprints.fields.instructions", "Instructions")}
              </DialogTitle>
            </DialogHeader>
            <TextareaAutosize
              id="blueprint-instructions"
              minRows={8}
              maxRows={10}
              autoFocus
              placeholder={t("blueprints.fields.instructionsPlaceholder")}
              className={`bg-input text-foreground placeholder:text-muted-foreground w-full resize-none overflow-y-auto rounded-[8px] border px-4 py-3 text-sm outline-none focus:border-primary ${
                errors.instructions || requiredErrors.instructions
                  ? "border-destructive"
                  : "border-border"
              }`}
              aria-invalid={Boolean(
                errors.instructions || requiredErrors.instructions,
              )}
              {...register("instructions", {
                onChange: (event) => {
                  if (event.target.value.trim()) {
                    clearErrors("instructions");
                    setRequiredErrors((current) => ({
                      ...current,
                      instructions: "",
                    }));
                  }
                },
              })}
            />
            {(errors.instructions?.message || requiredErrors.instructions) && (
              <p className="text-sm text-red-500">
                {errors.instructions?.message || requiredErrors.instructions}
              </p>
            )}
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="blueprint-description"
                className="text-foreground text-sm font-semibold"
              >
                {t("blueprints.fields.description", "Description")} (
                {t("common.optional")})
              </label>
              <TextareaAutosize
                id="blueprint-description"
                minRows={3}
                maxRows={3}
                placeholder={t(
                  "blueprints.fields.descriptionPlaceholder",
                  "Describe what this blueprint is used for",
                )}
                className={`bg-input text-foreground placeholder:text-muted-foreground w-full rounded-[8px] border px-4 py-3 overflow-y-auto text-sm outline-none focus:border-primary ${
                  errors.description ? "border-destructive" : "border-border"
                }`}
                aria-invalid={Boolean(errors.description)}
                {...register("description")}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="rounded-[8px]"
                onClick={() => setInstructionsDialogOpen(false)}
              >
                {t("common.cancel", "Cancel")}
              </Button>
              <Button
                type="button"
                className="rounded-[8px]"
                onClick={() => setInstructionsDialogOpen(false)}
              >
                {t("common.save", "Save")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default BlueprintDetailsDialog;
